import React, { useState, useEffect, useCallback } from "react";
import { Select, DatePicker, Empty, Tooltip, message } from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";
import { FiPhoneCall, FiMail, FiMessageCircle, FiUsers, FiActivity, FiSend, FiClock } from "react-icons/fi";
import { MdOutlineArrowUpward, MdOutlineArrowDownward } from "react-icons/md";
import DataParser from "../../../utlits/Dataparser";
import LeadPipelineService from "../../../Services/LeadPipelineService";

const { Option } = Select;
const { TextArea } = require("antd").Input;

// ── Config ───────────────────────────────────────────────────────────────────
const ACTIVITY_TYPES = [
  { value: "call",     label: "Call",     icon: <FiPhoneCall />,    color: "#3b82f6" },
  { value: "email",    label: "Email",    icon: <FiMail />,         color: "#8b5cf6" },
  { value: "whatsapp", label: "WhatsApp", icon: <FiMessageCircle />, color: "#10b981" },
  { value: "meeting",  label: "Meeting",  icon: <FiUsers />,        color: "#f59e0b" },
  { value: "other",    label: "Other",    icon: <FiActivity />,     color: "#6b7280" },
];

const OUTCOMES = [
  { value: "successful",         label: "Successful" },
  { value: "no_answer",          label: "No Answer" },
  { value: "voicemail",          label: "Voicemail" },
  { value: "callback_scheduled", label: "Callback Scheduled" },
  { value: "info_sent",          label: "Info Sent" },
  { value: "other",              label: "Other" },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function typeConfig(type) {
  return ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[4];
}

function outcomeLabel(v) {
  return OUTCOMES.find(o => o.value === v)?.label || v;
}

function formatLoggedAt(dateStr) {
  if (!dateStr) return "";
  const m = moment(dateStr);
  const now = moment();
  if (now.diff(m, "hours") < 24) return m.fromNow();
  return m.format("DD MMM YYYY, h:mm a");
}

// ── Component ────────────────────────────────────────────────────────────────
const LeadActivityLog = ({ leadId, companyId }) => {
  const currentUserId = DataParser.UserId ? DataParser.UserId() : 0;

  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [actType, setActType]       = useState("call");
  const [direction, setDirection]   = useState("outbound");
  const [outcome, setOutcome]       = useState(null);
  const [notes, setNotes]           = useState("");
  const [loggedAt, setLoggedAt]     = useState(moment());

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LeadPipelineService.getActivities(leadId, companyId);
      const list = Array.isArray(res)
        ? res
        : res?.activities || res?.data || [];
      setActivities(list);
    } catch {
      // silently fail — list stays empty
    } finally {
      setLoading(false);
    }
  }, [leadId, companyId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      message.warning("Please add a note before logging.");
      return;
    }
    setSubmitting(true);
    try {
      await LeadPipelineService.logActivity(leadId, {
        company_id:        companyId,
        logged_by_user_id: currentUserId,
        logged_at:         (loggedAt || moment()).toISOString(),
        activity_type:     actType,
        direction,
        outcome:           outcome || undefined,
        notes:             notes.trim(),
      });
      message.success("Activity logged.");
      setNotes("");
      setOutcome(null);
      setLoggedAt(moment());
      fetchActivities();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to log activity.");
    } finally {
      setSubmitting(false);
    }
  };

  const cfg = typeConfig(actType);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* ── Log activity form ── */}
      <div className="p-3 mb-3" style={{ background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
          Log Activity
        </div>

        {/* Row 1: Activity Type + Direction */}
        <div className="d-flex gap-2 mb-3">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={`float-select-wrap ${actType ? "has-value" : ""}`} style={{ height: 44 }}>
              <label>Activity Type</label>
              <Select placeholder="" value={actType} onChange={setActType}>
                {ACTIVITY_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={`float-select-wrap ${direction ? "has-value" : ""}`} style={{ height: 44 }}>
              <label>Direction</label>
              <Select placeholder="" value={direction} onChange={setDirection}>
                <Option value="outbound">Outbound</Option>
                <Option value="inbound">Inbound</Option>
              </Select>
            </div>
          </div>
        </div>

        {/* Row 2: Outcome + Activity Date */}
        <div className="d-flex gap-2 mb-3">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={`float-select-wrap ${outcome ? "has-value" : ""}`} style={{ height: 44 }}>
              <label>Outcome</label>
              <Select placeholder="" value={outcome} onChange={setOutcome} allowClear>
                {OUTCOMES.map((o) => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={`float-select-wrap ${loggedAt ? "has-value" : ""}`} style={{ height: 44 }}>
              <label>Activity Date</label>
              <DatePicker
                value={loggedAt}
                onChange={(v) => setLoggedAt(v || moment())}
                format="DD MMM YYYY"
                disabledDate={(d) => d && d > moment().endOf("day")}
                allowClear={false}
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className={`float-input-wrap mb-3 ${notes ? "has-value" : ""}`}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder=" "
            style={{ height: 60, resize: "none", borderRadius: 8, padding: "14px 14px 8px 14px" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSubmit();
            }}
          />
          <label>Notes for this {cfg.label.toLowerCase()}…</label>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="btn btn-primary waves-effect waves-light w-100"
          style={{
            height: 40, borderRadius: 8, background: submitting ? "#a3e6d0" : "#0DDFAB",
            border: "none", fontWeight: 700, fontSize: 14, display: "flex",
            alignItems: "center", justifyContent: "center", gap: 8,
            marginTop: 4,
          }}
        >
          <FiSend size={14} />
          {submitting ? "Logging…" : "Log Activity"}
        </button>
        <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "right", marginTop: 6 }}>
          Ctrl + Enter to submit
        </div>
      </div>

      {/* ── Activity timeline ── */}
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
        {loading ? (
          <div style={{ padding: '16px' }}>
            <Skeleton count={4} height={40} style={{ marginBottom: 8 }} />
          </div>
        ) : activities.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                No activities logged yet
              </span>
            }
          />
        ) : (
          <div>
            {activities.map((act, idx) => {
              const tc = typeConfig(act.activity_type);
              const isSystem = act.is_system_generated;

              return (
                <div
                  key={act.id || idx}
                  style={{
                    display: "flex", gap: 10, marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: idx < activities.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                    background: isSystem ? "#ede9fe" : `${tc.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: isSystem ? "#8b5cf6" : tc.color, fontSize: 13,
                  }}>
                    {isSystem ? <FiActivity size={13} /> : tc.icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {/* Type badge — show "Stage Change" for system, activity type otherwise */}
                        <span style={{
                          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.05em", padding: "1px 7px", borderRadius: 10,
                          background: isSystem ? "#ede9fe" : `${tc.color}18`,
                          color: isSystem ? "#8b5cf6" : tc.color,
                        }}>
                          {isSystem ? "Stage Change" : act.activity_type}
                        </span>

                        {/* Direction */}
                        {!isSystem && act.direction && (
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>
                            {act.direction === "inbound"
                              ? <MdOutlineArrowDownward style={{ color: "#10b981" }} />
                              : <MdOutlineArrowUpward style={{ color: "#f59e0b" }} />}
                          </span>
                        )}

                        {/* Outcome */}
                        {!isSystem && act.outcome && (
                          <span style={{
                            fontSize: 10, color: "#64748b",
                            background: "#f8fafc", padding: "1px 6px", borderRadius: 6,
                          }}>
                            {outcomeLabel(act.outcome)}
                          </span>
                        )}
                      </div>

                      {/* Timestamp */}
                      <Tooltip title={moment(act.logged_at).format("DD MMM YYYY, h:mm a")}>
                        <span style={{
                          fontSize: 10, color: "#94a3b8", flexShrink: 0,
                          display: "flex", alignItems: "center", gap: 3,
                        }}>
                          <FiClock size={9} />
                          {formatLoggedAt(act.logged_at)}
                        </span>
                      </Tooltip>
                    </div>

                    {/* Notes */}
                    {act.notes && (
                      <div style={{
                        fontSize: 12, color: "#475569", marginTop: 4,
                        lineHeight: 1.5, wordBreak: "break-word",
                      }}>
                        {act.notes}
                      </div>
                    )}

                    {/* Logger name — shown for all activities */}
                    {act.logged_by_name && (
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>
                        by {act.logged_by_name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadActivityLog;
